/**
 * Inline citation markers in an answer, and the reference cards they point at.
 *
 * The assistant may write `[1]`, `[2]` … against the numbered sources it was
 * given. Those markers are turned into `<sup data-citation="n">` so the renderer
 * can show them as chips that jump to reference card n, and the cards carry the
 * matching number.
 *
 * IMPORTANT: this only renders markers the model emits. Which card `[n]` lands
 * on is decided upstream — `core.citations.assign_citation_numbers` numbers the
 * model's KNOWLEDGE over exactly the set that becomes cards, so the two agree.
 * A number outside that set is therefore a model slip rather than an off-by-one,
 * and the renderer drops it: see `makeCitationBadge` in `components/Markdown`.
 * Markers carrying no number at all are removed here, before matching.
 */

/** DOM id of the reference card a `[n]` marker jumps to. */
export const citationAnchorId = (n: number): string => `chat-reference-${n}`;

/**
 * Match a standalone citation marker: `[12]`, and also the grouped forms models
 * reach for on their own — `[1,2]`, `[1، 2]` (Arabic comma), `[1; 2]`.
 *
 * Grouping is accepted rather than fought because no amount of prompting stops a
 * model from occasionally combining sources, and a group left unmatched would
 * reach the reader as literal `[1,2]` text. `[1][2]` needs nothing special: it is
 * simply two consecutive matches.
 *
 * Excludes `[1](…)`, which is markdown link syntax, and `[1]:`, a link
 * definition — rewriting either would break the link rather than decorate a
 * citation. A following `[` is only treated as link syntax (`[text][ref]`) when
 * what follows it is not a digit, so stacked `[1][2]` citations still match while
 * a real reference link is left alone.
 */
const CITATION_MARKER = /\[(\d{1,3}(?:\s*[,;،]\s*\d{1,3})*)\](?![(:])(?!\[(?!\d))/g;

/** Separators allowed between numbers inside one marker. */
const GROUP_SEPARATOR = /[\s,;،]+/;

/** Spans of the source that must be left exactly as written. */
const PROTECTED = /(```[\s\S]*?```|~~~[\s\S]*?~~~|`[^`\n]*`)/g;

/**
 * A marker carrying no usable number: empty brackets, or one of the placeholder
 * phrases a model reaches for when it has nothing to cite.
 *
 * There is no number in these to resolve, so no badge can ever stand in for one
 * and it reaches the reader as literal text. `[no citation number]` in
 * particular was the label this product printed on an uncitable KNOWLEDGE entry,
 * and models copied it into the prose verbatim; the label is bracket-free now,
 * but answers already stored in conversation history still carry the old text,
 * so it is stripped here rather than only fixed upstream.
 *
 * Stripping runs *before* the real markers are matched, which also repairs the
 * neighbour: in `[1][no citation number]` the `[1]` is left unmatched by
 * `CITATION_MARKER`, since `[` followed by a non-digit is markdown
 * reference-link syntax. Remove the placeholder and that `[1]` becomes a badge.
 *
 * The preceding space goes with it, so a stripped end-of-paragraph marker cannot
 * leave two trailing spaces behind — in markdown that is a hard line break. No
 * alternative contains a digit, so a real `[1]` cannot match; `[(` and `[:` are
 * excluded as link syntax exactly as in `CITATION_MARKER`; and the leading
 * capture keeps `- [ ]` task-list checkboxes out of the empty-bracket case.
 */
const UNUSABLE_MARKER =
  /(^[ \t]*(?:[-*+]|\d+[.)])[ \t]+)?[ \t]*\[[ \t]*(?:no[ \t]+citations?(?:[ \t]+numbers?)?|citations?[ \t]+needed|no[ \t]+markers?|background|n)?[ \t]*\](?![(:])/gim;

/**
 * Drop every marker that carries no number a reference card could match.
 *
 * The optional leading group is a list bullet. A bracket sitting directly after
 * one is a task-list checkbox — `- [ ] todo` — not a citation, so that match is
 * handed back untouched; everything else is removed.
 */
const stripUnusableMarkers = (text: string): string =>
  text.replace(UNUSABLE_MARKER, (match: string, listPrefix?: string) =>
    listPrefix ? match : '',
  );

/**
 * A run of consecutive badges, whitespace allowed between them.
 *
 * Matched as a run rather than with a backreference so the duplicate check can be
 * done in code: any repeat of a number already seen in the run is dropped.
 */
const BADGE_RUN = /(?:<sup data-citation="\d{1,3}">\d{1,3}<\/sup>\s*)+/g;

/** The number carried by one badge. */
const BADGE_NUMBER = /data-citation="(\d{1,3})"/g;

/** Drop any number already used earlier in the same run of badges. */
const dedupeBadgeRun = (run: string): string => {
  const seen = new Set<string>();
  const kept: string[] = [];

  for (const [, n] of run.matchAll(BADGE_NUMBER)) {
    if (seen.has(n)) continue;
    seen.add(n);
    kept.push(`<sup data-citation="${n}">${n}</sup>`);
  }

  // Keep whatever trailing whitespace the run ended with, so the words either
  // side of it do not get glued together.
  const trailing = run.slice(run.lastIndexOf('</sup>') + '</sup>'.length);
  return kept.join('') + trailing;
};

/**
 * Rewrite citation markers as one `<sup data-citation="n">n</sup>` per source.
 *
 * A grouped marker becomes one badge per number, deduplicated: `[1,1,2]` yields
 * a badge for 1 and one for 2, because the same source repeated inside a single
 * marker is a model slip, not two citations, and rendering it twice would read as
 * two different sources.
 *
 * Code spans and fenced blocks are passed through untouched — a `[0]` inside a
 * code sample is source text, not a citation.
 */
export const linkifyCitations = (markdown: string): string => {
  if (!markdown.includes('[')) return markdown;

  // Split on the protected spans so the replacement only runs on prose. The
  // capture group means the delimiters themselves land in the array, at odd
  // indexes, and are re-joined unchanged.
  const linked = markdown
    .split(PROTECTED)
    .map((part, i) =>
      i % 2 === 1
        ? part
        : stripUnusableMarkers(part).replace(CITATION_MARKER, (_match, group: string) => {
            const numbers = [
              ...new Set(group.split(GROUP_SEPARATOR).filter(Boolean)),
            ];
            return numbers
              .map((n) => `<sup data-citation="${n}">${n}</sup>`)
              .join('');
          }),
    )
    .join('');

  // Collapse a source cited twice in a row (`[1][1]`, or `[1]` ending one
  // sentence and opening the next). The prompt asks for one marker set per
  // paragraph, but a model repeating itself would otherwise render the same
  // source as two adjacent badges, which reads as two different sources.
  return linked.replace(BADGE_RUN, dedupeBadgeRun);
};
